import { Button } from '@capturi/ui-components'
import {
  Box,
  Flex,
  FormLabel,
  Input,
  Select,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { API_PREFIX } from 'config'
import { useCopilotSettings } from 'copilot/useSettings'
import { setupWorker } from 'msw/browser'
import {
  type ChangeEvent,
  type FC,
  type ReactNode,
  useEffect,
  useState,
} from 'react'

import type { CallFlow, WidgetState } from '../Copilot'
import { type DelayMode, handlers } from '../mockServer'
import { deserializeState } from '../sessionPersistence'
import { clearFeed, hydrateFeed } from '../useFeed'
import { Widget } from '../Widget'
import savedSessionFixture from './saved-session.json'

const callFlows = ['awaiting', 'alerting', 'handling', 'wrapUp'] as const
const modules = [
  'previousInteractions',
  'handover',
  'voiceBotHandover',
  'askCopilot',
  'liveSummary',
] as const
const config = ['waitUntilAnswer'] as const

const Header: FC<{ children: ReactNode }> = ({ children }) => (
  <Text
    borderBottom="2px solid #ffc100"
    p="1"
    bg="primary.700"
    color="white"
    fontWeight="bold"
    width="fit-content"
  >
    {children}
  </Text>
)

const defaultState: WidgetState = {
  currentCallState: 'handling',
  data: {
    customer: '51365107',
    sessionUid: 'sik_tester',
    queue: 'test_queue',
    hasTranscription: true,
    getTranscription: () => '',
  },
  modules: {
    handover: { enabled: true, show: true },
    voiceBotHandover: { enabled: true, show: true },
    askCopilot: { enabled: true },
    previousInteractions: { enabled: true },
    liveSummary: {
      enabled: true,
      lockSummaryEdit: false,
      buttonDisabled: false,
      reason: '',
    },
  },
  config: {
    waitUntilAnswer: false,
    legacyApiToken: '',
    copilotApiToken:
      'pzl_copilot_669a1efc-7f39-404a-a3ec-adbd1a465327_ZTfpDVYpPQZJLU21UzVVNtpq7Up3NybFs2U8i95DzLJsRPUg_XF3HlEzf6LPh0Z9Z67ihy81oNIGANQwn398iA',
  },
}

const getStateFromUrl = (key: string): WidgetState => {
  const urlParams = new URLSearchParams(window.location.search)
  const stateParam = urlParams.get(key)
  if (stateParam) {
    const parsed = JSON.parse(decodeURIComponent(stateParam))
    return {
      ...parsed,
      // A URL saved before a module existed omits its key; Widget.tsx reads
      // every module unconditionally
      modules: { ...defaultState.modules, ...parsed.modules },
      data: {
        ...parsed.data,
        // Restore the getTranscription function that was lost during JSON serialization
        getTranscription: () => '',
      },
    }
  }
  return defaultState
}

const setStateToUrl = (key: string, state: WidgetState) => {
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.set(key, encodeURIComponent(JSON.stringify(state)))
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`
  window.history.replaceState({}, '', newUrl)
}

const server = setupWorker()
let serverStartPromise: ReturnType<typeof server.start> | null = null
const defaultLiveSummaryDisabledReason =
  'You need to answer the call before you can create a live summary'

export const TestBench: FC = () => {
  const [state, setState] = useState<WidgetState>(getStateFromUrl('state'))
  const [useMockApi, setUseMockApi] = useState(true)
  const [serverReady, setServerReady] = useState(false)
  const [delayMode, setDelayMode] = useState<DelayMode>('real')
  const [errorCodeInput, setErrorCodeInput] = useState('500')

  const token = useCopilotSettings(state.config.copilotApiToken)
  const queryClient = useQueryClient()

  // Update URL search params when state changes
  useEffect(() => {
    setStateToUrl('state', state)
  }, [state])

  // Manage MSW server lifecycle
  useEffect(() => {
    let isActive = true

    const setupMockServer = async () => {
      try {
        // Start server if not already starting/started
        if (!serverStartPromise) {
          serverStartPromise = server.start({
            onUnhandledRequest: 'bypass',
            quiet: true,
            serviceWorker: { url: '/mockServiceWorker.js' },
          })
        }

        // Always wait for the server to be fully started
        await serverStartPromise

        // Ensure service worker is controlling the page before proceeding
        // This is the key fix - we need to wait for the SW to be active
        if (navigator.serviceWorker.controller === null) {
          await new Promise<void>((resolve) => {
            navigator.serviceWorker.addEventListener(
              'controllerchange',
              () => resolve(),
              { once: true },
            )
          })
        }

        if (isActive) {
          server.resetHandlers()
          const parsed = Number.isNaN(Number(errorCodeInput))
            ? undefined
            : Number.parseInt(errorCodeInput, 10)
          server.use(
            ...handlers({
              delayMode,
              errorCode: parsed,
              apiPrefix: API_PREFIX,
            }),
          )
          setServerReady(true)
        }
      } catch (error) {
        console.error('Failed to start MSW server:', error)
        serverStartPromise = null
      }
    }

    if (useMockApi) {
      setServerReady(false)
      setupMockServer()
    } else {
      if (serverStartPromise) {
        server.stop()
        serverStartPromise = null
      }
      setServerReady(false)
    }

    return () => {
      isActive = false
    }
  }, [useMockApi, delayMode, errorCodeInput])

  const handleToggleMockApi = (e: ChangeEvent<HTMLInputElement>): void => {
    setUseMockApi(e.target.checked)
    queryClient.clear()
    clearFeed()
  }

  const handleReset = () => {
    // Clear local caches and feed
    queryClient.clear()
    clearFeed()

    // Re-apply MSW handlers to reset in-memory mock state
    if (useMockApi && serverStartPromise) {
      server.resetHandlers()
      const parsed = Number.isNaN(Number(errorCodeInput))
        ? undefined
        : Number.parseInt(errorCodeInput, 10)
      server.use(
        ...handlers({ delayMode, errorCode: parsed, apiPrefix: API_PREFIX }),
      )
    }
  }

  const handleLoadSavedSession = async () => {
    try {
      const hydrated = deserializeState(savedSessionFixture)
      if (!hydrated) {
        throw new Error('Invalid saved-session fixture')
      }

      hydrateFeed(hydrated.session.uid, hydrated.feed)
      setState((prev) => ({
        ...prev,
        currentCallState: hydrated.session.callState,
        data: {
          ...prev.data,
          customer: hydrated.session.customer,
          sessionUid: hydrated.session.uid,
          queue: hydrated.session.queue,
          hasTranscription: hydrated.session.hasTranscription,
          getTranscription: () => hydrated.session.transcription,
        },
      }))
    } catch (error) {
      console.error('Failed to load saved session fixture', error)
    }
  }

  const updateLiveSummary = (
    updater: (
      liveSummary: Extract<
        WidgetState['modules']['liveSummary'],
        { enabled: true }
      >,
    ) => Extract<WidgetState['modules']['liveSummary'], { enabled: true }>,
  ) => {
    setState((s) => {
      if (!s.modules.liveSummary.enabled) return s

      return {
        ...s,
        modules: {
          ...s.modules,
          liveSummary: updater(s.modules.liveSummary),
        },
      }
    })
  }

  const renderButton = (s: CallFlow) => {
    return (
      <Button
        key={s}
        colorScheme={state.currentCallState === s ? 'primary' : 'greys'}
        borderRadius={0}
        onClick={() => setState((state) => ({ ...state, currentCallState: s }))}
      >
        {s}
      </Button>
    )
  }

  const renderModuleSwitch = (module: (typeof modules)[number]) => {
    const moduleConfig = state.modules[module]
    const isEnabled = moduleConfig?.enabled || false

    return (
      <Flex key={module}>
        <Switch
          colorScheme="primary"
          p={1}
          mr="2"
          onChange={(e) => {
            setState((s) => ({
              ...s,
              modules: {
                ...s.modules,
                [module]: { ...s.modules[module], enabled: e.target.checked },
              },
            }))
          }}
          isChecked={isEnabled}
        />
        <FormLabel htmlFor={module}>{module}</FormLabel>
      </Flex>
    )
  }
  const renderConfigSwitch = (conf: (typeof config)[number]) => {
    return (
      <Flex key={conf}>
        <Switch
          colorScheme="primary"
          p={1}
          mr="2"
          onChange={(e) => {
            setState((s) => ({
              ...s,
              config: { ...s.config, [conf]: e.target.checked },
            }))
          }}
          isChecked={state.config[conf]}
        />
        <FormLabel htmlFor={conf}>{conf}</FormLabel>
      </Flex>
    )
  }

  if (useMockApi && !serverReady) {
    return null
  }

  const liveSummaryIsEnabled = state.modules.liveSummary.enabled
  const liveSummaryButtonDisabled =
    liveSummaryIsEnabled && state.modules.liveSummary.buttonDisabled
  const liveSummaryReason =
    liveSummaryIsEnabled && state.modules.liveSummary.reason
      ? state.modules.liveSummary.reason
      : ''

  return (
    <Flex height="100vh">
      <Box
        p="4"
        borderRight="2px solid greys"
        bg="#e1dae5"
        fontFamily="monospace"
      >
        <Box mb="8%">
          <Flex alignItems="center" justifyContent="space-between" mb="2">
            <Header>Network</Header>
            <Button
              size="sm"
              colorScheme="primary"
              onClick={handleReset}
              data-testid="testbench-reset-button"
            >
              Reset
            </Button>
          </Flex>
          <Box bg="greys.50" p="2">
            <Flex>
              <Switch
                id="useMockApi"
                colorScheme="primary"
                p={1}
                mr="2"
                onChange={handleToggleMockApi}
                isChecked={useMockApi}
              />
              <FormLabel htmlFor="useMockApi">Mock network requests</FormLabel>
            </Flex>
          </Box>
          <Tooltip
            label={
              !useMockApi
                ? 'Enable mock server to change delay mode'
                : undefined
            }
          >
            <Select
              isDisabled={!useMockApi}
              borderRadius="0"
              bg="greys.50"
              value={delayMode}
              onChange={(e) => {
                const value = Number.isNaN(Number(e.target.value))
                  ? e.target.value
                  : Number(e.target.value)
                setDelayMode(value as DelayMode)
              }}
            >
              <option value="real">Real</option>
              <option value="infinite">Infinite</option>
              <option value="error">Error</option>
              <option value={300}>300ms</option>
              <option value={3000}>3000ms</option>
              <option value={5000}>5000ms</option>
              <option value={7000}>7000ms</option>
              <option value={10_000}>10.000ms</option>
            </Select>
            {useMockApi && delayMode === 'error' && (
              <Flex alignItems="center" gap="2" mt="2">
                <FormLabel htmlFor="error_code" m={0} minW="9rem">
                  HTTP error status
                </FormLabel>
                <Input
                  id="error_code"
                  type="text"
                  borderRadius="0"
                  bg="greys.50"
                  value={errorCodeInput}
                  onChange={(e) => {
                    const raw = e.target.value
                    setErrorCodeInput(raw)
                    if (serverStartPromise) {
                      server.resetHandlers()
                      const parsed = Number.isNaN(Number(raw))
                        ? undefined
                        : Number.parseInt(raw, 10)
                      server.use(
                        ...handlers({
                          delayMode,
                          errorCode: parsed,
                          apiPrefix: API_PREFIX,
                        }),
                      )
                    }
                  }}
                />
              </Flex>
            )}
          </Tooltip>
        </Box>
        <Box mt="6">
          <Header>Session</Header>
          <Box
            bg="greys.50"
            p="2"
            display="flex"
            flexDirection="column"
            gap="2"
          >
            <Button
              size="sm"
              colorScheme="primary"
              onClick={handleLoadSavedSession}
              isDisabled={!useMockApi}
              data-testid="testbench-load-saved-session"
            >
              Load saved-session.json
            </Button>
            {!useMockApi && (
              <Text fontSize="xs" color="gray.600">
                Enable mock server to load the bundled fixture.
              </Text>
            )}
          </Box>
        </Box>

        <Box mt="8">
          <Header>Call Flow</Header>
          <Flex mb="8">{callFlows.map(renderButton)}</Flex>
        </Box>

        <Header>Customer</Header>
        <Input
          list="customer_numbers"
          bg="greys.50"
          borderRadius="0"
          value={state.data.customer}
          onChange={(e) => {
            const value = e.target.value
            setState((s) => ({
              ...s,
              data: { ...s.data, customer: value },
            }))
          }}
        />

        <datalist id="customer_numbers">
          <option value="52511603" />
        </datalist>

        <Box mt="20%">
          <Header>Modules</Header>
          <Box bg="greys.50" p="2">
            {modules.map(renderModuleSwitch)}
          </Box>
        </Box>
        <Box mt="8">
          <Header>Live Summary Button</Header>
          <Box bg="greys.50" p="2">
            <Flex>
              <Switch
                id="testbench-live-summary-button-disabled"
                colorScheme="primary"
                p={1}
                mr="2"
                isDisabled={!liveSummaryIsEnabled}
                isChecked={liveSummaryButtonDisabled}
                onChange={(e) => {
                  const checked = e.target.checked
                  updateLiveSummary((liveSummary) =>
                    checked
                      ? {
                          ...liveSummary,
                          buttonDisabled: true as const,
                          reason:
                            liveSummary.reason ||
                            defaultLiveSummaryDisabledReason,
                        }
                      : {
                          ...liveSummary,
                          buttonDisabled: false as const,
                          reason: '',
                        },
                  )
                }}
              />
              <FormLabel htmlFor="testbench-live-summary-button-disabled">
                Force disabled with reason
              </FormLabel>
            </Flex>
            <Input
              id="testbench-live-summary-button-reason"
              borderRadius="0"
              bg="greys.50"
              placeholder="Disabled reason shown as inline text"
              value={liveSummaryReason}
              isDisabled={!liveSummaryIsEnabled || !liveSummaryButtonDisabled}
              onChange={(e) => {
                const value = e.target.value
                updateLiveSummary((liveSummary) =>
                  value.trim().length > 0
                    ? {
                        ...liveSummary,
                        buttonDisabled: true as const,
                        reason: value,
                      }
                    : {
                        ...liveSummary,
                        buttonDisabled: false as const,
                        reason: '',
                      },
                )
              }}
            />
            {!liveSummaryIsEnabled && (
              <Text fontSize="xs" color="gray.600" mt="2">
                Enable the liveSummary module above to test this button.
              </Text>
            )}
          </Box>
        </Box>
        <Box mt="20%">
          <Header>Config</Header>
          <Box bg="greys.50" p="2">
            {config.map(renderConfigSwitch)}
          </Box>
        </Box>

        <Box mt="40%">
          <Header>Token Org uid</Header>
          <Box bg="greys.50" p="2">
            <Text>{token.data?.organizationUid}</Text>
          </Box>
        </Box>
      </Box>
      <Widget state={state} />
    </Flex>
  )
}
